import React, { useCallback, useState } from 'react'
import { Box, Button, Checkbox, FormControl, FormLabel, Input, Heading, VStack, useToast, Text, Grid, GridItem, Flex, HStack } from '@chakra-ui/react'
import { signInWithEmailAndPassword } from 'firebase/auth'
import { auth } from '../../firebaseConfig'
import { ReactComponent as Logo } from './../../assets/logo.svg'
import { ReactComponent as Illustration } from './../../assets/LoginIllustration.svg'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '../../store/store'
import { setTokenAndRole } from '../../store/slices/authSlice'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { getErrorMessage } from '../../utils/utils'
import { createDashboardRolesApi } from '../../utils/api-interface'
import { log } from '../../utils/logger'

function Login(): JSX.Element {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const dispatch = useDispatch<AppDispatch>()
  const toast = useToast()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setEmail(e.target.value.trim())
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e.target.value.endsWith('\n')) {
      log.debug(`Removing \\n`)
      return
    }
    setPassword(e.target.value.trim())
  }

  const handleRememberMeChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setRememberMe(e.target.checked)
  }

  const [onResetPassword, isOnResetPassword] = useState<boolean>(false)

  const handleSubmit = useCallback(async (): Promise<void> => {
    if (isLoading) {
      return
    }
    setIsLoading(true)
    try {
      if (onResetPassword) {
        await createDashboardRolesApi().adminResetPassword({
          email: email.trim().toLowerCase()
        })
        toast({
          title: t('reset_password.link_success_title'),
          description: t('reset_password.link_success_text'),
          status: 'success',
          duration: 5000,
          isClosable: true
        })
      } else {
        const userCredential = await signInWithEmailAndPassword(auth, email.trim(), password.trim())
        const user = userCredential.user
        const token = await user.getIdToken(true)
        const tokenResult = await user.getIdTokenResult()
        const claims = tokenResult.claims
        const role = claims.role ? String(claims.role) : null

        if (
          process.env.REACT_APP_ROLE_ADMINISTRATOR &&
          process.env.REACT_APP_ROLE_BACKOFFICE &&
          process.env.REACT_APP_ROLE_OFFICE &&
          role &&
          (role === process.env.REACT_APP_ROLE_ADMINISTRATOR || role === process.env.REACT_APP_ROLE_BACKOFFICE || role === process.env.REACT_APP_ROLE_OFFICE)
        ) {
          dispatch(setTokenAndRole({ token, role, expirationTime: tokenResult.expirationTime }))
        } else {
          throw new Error(t('login_no_auth'))
        }
        toast({
          title: t('login_successful'),
          description: `${t('login_welcome')}, ${user.displayName}`,
          status: 'success',
          duration: 5000,
          isClosable: true
        })
        navigate('/graph-dashboard')
      }
    } catch (error: any) {
      log.error(`[Login] error ${onResetPassword ? 'reset' : 'login'}: ${JSON.stringify(error)}`)
      toast({
        title: onResetPassword ? t('reset_password.error') : t('login_error'),
        description: getErrorMessage(error),
        status: 'error',
        duration: 5000,
        isClosable: true
      })
    }
    setIsLoading(false)
  }, [isLoading, onResetPassword, email, password, toast, t, navigate, dispatch])

  return (
    <Grid templateColumns='repeat(3, 1fr)' gap={6} minH='100vh'>
      <GridItem colSpan={1} />
      <GridItem colSpan={1}>
        <Grid templateRows='repeat(3, 1fr)' h='full' gap={4}>
          <GridItem>
            <Flex h='full' justifyContent='center' alignItems='center'>
              <Logo />
            </Flex>
          </GridItem>
          <GridItem>
            <VStack as='form' alignItems={'self-start'}>
              <Heading color='neutralDark' fontStyle='normal' fontWeight='700' fontSize='26px' lineHeight='31.2px' marginBottom={'20px'}>
                {t('login_title')}
              </Heading>
              <FormControl id='email' isRequired={true} marginBottom={'24px'}>
                <FormLabel
                  requiredIndicator={true}
                  color='greyCold200'
                  textTransform='uppercase'
                  fontWeight='700'
                  fontSize='12px'
                  lineHeight='14px'
                  marginBottom={'8px'}>
                  {t('email')}
                </FormLabel>
                <Input
                  bg='greyNeutral100'
                  type='email'
                  value={email.trim()}
                  onChange={handleEmailChange}
                  onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>): void => {
                    if (event.key === 'Enter') {
                      handleSubmit()
                    }
                  }}
                  placeholder='email@example.com'
                  _placeholder={{ color: 'greyCold200', fontWeight: '400', fontSize: '16px', lineHeight: '22.4px' }}
                />
              </FormControl>
              {!onResetPassword && (
                <FormControl id='password' isRequired={true} marginBottom={'20px'}>
                  <FormLabel
                    requiredIndicator={true}
                    color='greyCold200'
                    textTransform='uppercase'
                    fontWeight='700'
                    fontSize='12px'
                    lineHeight='14px'
                    marginBottom={'8px'}>
                    {t('password')}
                  </FormLabel>
                  <Input
                    bg='greyNeutral100'
                    type='password'
                    value={password}
                    onChange={handlePasswordChange}
                    onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>): void => {
                      if (event.key === 'Enter') {
                        handleSubmit()
                      }
                    }}
                    placeholder='password'
                    _placeholder={{ color: 'greyCold200', fontWeight: '400', fontSize: '16px', lineHeight: '22.4px' }}
                  />
                </FormControl>
              )}
              {onResetPassword ? (
                <HStack justifyContent={'space-between'} width={'100%'}>
                  <div></div>
                  <Text
                    color='primary.500'
                    fontWeight='500'
                    fontSize='12px'
                    lineHeight='14px'
                    marginBottom={'22px'}
                    cursor={'pointer'}
                    onClick={(): void => isOnResetPassword(false)}>
                    {t('cancel')}
                  </Text>
                </HStack>
              ) : (
                <HStack justifyContent={'space-between'} width={'100%'}>
                  <Checkbox
                    isChecked={rememberMe}
                    onChange={handleRememberMeChange}
                    _checked={{
                      '& .chakra-checkbox__control': { background: 'neutralTrasparentDark' }
                    }}
                    marginBottom={'22px'}>
                    <Text color='greyCold500' fontWeight='500' fontSize='12px' lineHeight='14px'>
                      {t('remember_me')}
                    </Text>
                  </Checkbox>
                  <Text
                    color='primary.500'
                    fontWeight='500'
                    fontSize='12px'
                    lineHeight='14px'
                    marginBottom={'22px'}
                    cursor={'pointer'}
                    onClick={(): void => isOnResetPassword(true)}>
                    {t('forgot_password')}
                  </Text>
                </HStack>
              )}
              <Button bg='neutralDark' isLoading={isLoading} variant={'login'} onClick={handleSubmit}>
                {onResetPassword ? t('send_link') : t('login_button')}
              </Button>
            </VStack>
          </GridItem>
          <GridItem />
        </Grid>
      </GridItem>
      <GridItem colSpan={1} position={'fixed'} right={0} bottom={0}>
        <Flex h='full' direction='column' justifyContent='flex-end' alignItems='center'>
          <Box display='flex' alignItems='center' justifyContent='center'>
            <Illustration />
          </Box>
        </Flex>
      </GridItem>
    </Grid>
  )
}

export { Login }
